import { InjectionToken, NgModule } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Analytics, getAnalytics } from 'firebase/analytics';
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, Firestore, initializeFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, Functions, getFunctions } from 'firebase/functions';
import { getRemoteConfig, RemoteConfig } from 'firebase/remote-config';
import { connectStorageEmulator, FirebaseStorage, getStorage } from 'firebase/storage';
import { environment } from '../../environments/environment';

export const FirebaseToken = {
  Analytics: new InjectionToken<Analytics>('FirebaseAnalytics'),
  App: new InjectionToken<FirebaseApp>('FirebaseApp'),
  Auth: new InjectionToken<Auth>('FirebaseAuth'),
  Firestore: new InjectionToken<Firestore>('FirebaseFirestore'),
  Functions: new InjectionToken<Functions>('FirebaseFunctions'),
  RemoteConfig: new InjectionToken<RemoteConfig>('FirebaseRemoteConfig'),
  Storage: new InjectionToken<FirebaseStorage>('FirebaseStorage'),
};

@NgModule({
  providers: [
    {
      provide: FirebaseToken.App,
      deps: [],
      useFactory: () => initializeApp(environment.firebase.options),
    },
    {
      provide: FirebaseToken.Analytics,
      deps: [FirebaseToken.App],
      useFactory: (app: FirebaseApp) => getAnalytics(app),
    },
    {
      provide: FirebaseToken.Auth,
      deps: [FirebaseToken.App],
      useFactory: (app: FirebaseApp) => {
        const auth = getAuth(app);

        if (environment.firebase.emulation) {
          connectAuthEmulator(auth, environment.firebase.emulation.auth.url);
        }

        return auth;
      },
    },
    {
      provide: FirebaseToken.Firestore,
      deps: [FirebaseToken.App],
      useFactory: (app: FirebaseApp) => {
        // TODO: Remove this? Looks like the Safari 26.4 WebKit engine + Firebase don't like each other, apparently enabling `experimentalForceLongPolling` fixes the issue.
        // Issue: https://github.com/firebase/firebase-js-sdk/issues/9789
        //
        // The bug lives in the WebKit engine, so it affects every browser/web view
        // built on it: desktop Safari, mobile Safari, SFSafariViewController and the
        // WKWebView the UploadCam iOS app uses to embed the team admin panel. We
        // therefore can't rely on the "Safari" UA branding (WKWebView strips both the
        // "Safari" and "Version/" tokens); we detect the affected engine instead.
        const isAffectedVersion = (major: number, minor: number) =>
          (major === 26 && minor >= 4) || major > 26;

        const isAffectedWebKit = () => {
          const ua = navigator.userAgent;

          // Desktop & standalone Safari (incl. SFSafariViewController and iPadOS
          // desktop mode) expose a "Version/26.4 … Safari" token.
          const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
          const safariVersion = ua.match(/Version\/(\d+)\.(\d+)/);
          if (isSafari && safariVersion) {
            if (isAffectedVersion(parseInt(safariVersion[1], 10), parseInt(safariVersion[2], 10))) {
              return true;
            }
          }

          // iOS/iPadOS WebKit (mobile Safari and, crucially, the app's WKWebView)
          // omits the "Version/"/"Safari" tokens. Every web view on iOS shares the
          // same WebKit engine, so fall back to the OS version reported in the UA,
          // e.g. "CPU iPhone OS 26_4 like Mac OS X" / "CPU OS 26_4 like Mac OS X".
          const iosVersion = ua.match(/(?:iPhone|iPad|iPod).*?\bOS (\d+)_(\d+)\b/);
          if (iosVersion) {
            if (isAffectedVersion(parseInt(iosVersion[1], 10), parseInt(iosVersion[2], 10))) {
              return true;
            }
          }

          return false;
        };

        const firestore = initializeFirestore(app, {
          experimentalForceLongPolling: isAffectedWebKit(),
          ignoreUndefinedProperties: true,
        });

        if (environment.firebase.emulation) {
          connectFirestoreEmulator(
            firestore,
            environment.firebase.emulation.firestore.host,
            environment.firebase.emulation.firestore.port,
          );
        }

        return firestore;
      },
    },
    {
      provide: FirebaseToken.Functions,
      deps: [FirebaseToken.App],
      useFactory: (app: FirebaseApp) => {
        const functions = getFunctions(app);

        if (environment.firebase.emulation) {
          connectFunctionsEmulator(
            functions,
            environment.firebase.emulation.functions.host,
            environment.firebase.emulation.functions.port,
          );
        }

        return functions;
      },
    },
    {
      provide: FirebaseToken.RemoteConfig,
      deps: [FirebaseToken.App],
      useFactory: (app: FirebaseApp) => {
        const remoteConfig = getRemoteConfig(app);

        if (environment.env === 'development') {
          remoteConfig.settings.minimumFetchIntervalMillis = 300_000; // 5 minutes
        }

        return remoteConfig;
      },
    },
    {
      provide: FirebaseToken.Storage,
      deps: [FirebaseToken.App],
      useFactory: (app: FirebaseApp) => {
        const storage = getStorage(app);

        if (environment.firebase.emulation) {
          connectStorageEmulator(
            storage,
            environment.firebase.emulation.storage.host,
            environment.firebase.emulation.storage.port,
          );
        }

        return storage;
      },
    },
  ],
})
export class FirebaseModule {}
